import React, { useCallback, useMemo } from 'react';
import { Platform } from 'react-native';
import { AutoSizeTextarea } from 'web/src/administration/components/worksheet/dynamic-grid/auto-size-textarea';
import { createUseStyles } from 'react-jss';
import { DynamicGridCell } from 'web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-cell';
import { DynamicGridHeaderCell } from 'web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-header-cell';
import { DynamicGridRow } from 'web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-row';
import { FormElementGridCellAnswer } from '@packages/models/api';
import { Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';

interface SubColumnType {
    worksheetElementGridId: string;
    subColumnLabel: string;
    columnOrder: number;
    defaultValue: string;
    isRequired: boolean;
}

interface ColumnType {
    columnLabel: string;
    subColumns: SubColumnType[];
}

export interface SubColumnGridType {
    rowLabel: string;
    rowOrder: number;
    subRows: [];
    columns: ColumnType[];
}

interface ValveClearanceGridProps {
    cornerHeader: string;
    onChange?: (worksheetElementGridId: string, value: string) => void;
    onBlur?: (worksheetElementGridId: string) => void;
    readOnly?: boolean;
    rows: SubColumnGridType[];
    answers?: FormElementGridCellAnswer[];
    isCellInvalid?: (worksheetElementGridId: string) => boolean;
}

const useStyles = createUseStyles({
    container: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        overflowX: 'auto',
    },
    table: {
        height: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
        borderSpacing: 0,
        minWidth: '100%',
        border: `1px solid ${colors.grayThree}`,
    },
    header: {
        '& th:not(:last-child)': {
            borderRight: `2px solid ${colors.grayThree}`,
        },
    },
    subRowHeader: {
        '& th:first-child': {
            borderRight: `2px solid ${colors.grayThree}`,
            borderTop: `2px solid ${colors.grayThree}`,
        },
        '& th:nth-child(odd):not(:first-child)': {
            '&:not(:last-child)': {
                borderRight: `2px solid ${colors.grayThree}`,
            },
            borderLeft: `1px solid ${colors.grayThree}`,
        },

        '& th:not(:first-child)': {
            borderTop: `1px solid ${colors.grayThree}`,
            borderBottom: `1px solid ${colors.grayThree}`,
        },
    },
    bodyRow: {
        '& th:first-child': {
            borderRight: `2px solid ${colors.grayThree}`,
            borderTop: `2px solid ${colors.grayThree}`,
        },
        '& td:nth-child(odd):not(:first-child):not(:last-child)': {
            borderRight: `2px solid ${colors.grayThree}`,
        },
        '& td:not(:first-child)': {
            borderBottom: `1px solid ${colors.grayThree}`,
        },
    },
    cell: {
        minWidth: 90,
        width: 90,
    },
    subColumnCell: {
        minWidth: 62,
        width: 62,
    },

    borderLeft: {
        borderLeft: `1px solid ${colors.grayThree}`,
    },
    headerContent: {
        padding: '14px',
        display: 'block',
    },
});

export function ValveClearanceGrid({
    cornerHeader = '',
    onChange,
    onBlur,
    readOnly,
    rows,
    answers = [],
    isCellInvalid,
}: ValveClearanceGridProps) {
    const classes = useStyles();

    const focusTextarea = useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
        const textarea = e.currentTarget.querySelector('textarea');
        if (textarea && !textarea.disabled) textarea.focus();
    }, []);

    const headerColumns = useMemo(() => rows?.[0]?.columns ?? [], [rows]);

    if (!rows?.length || !headerColumns.length) {
        return null;
    }

    if (Platform.OS !== 'web') {
        return null;
    }

    return (
        <div className={classes.container}>
            <table className={classes.table}>
                <thead>
                    <DynamicGridRow className={classes.header}>
                        <DynamicGridHeaderCell className={`${classes.cell}`} contentClassName={classes.headerContent}>
                            <Typography style={{ fontWeight: 500 }} color={readOnly ? 'grayFive' : 'textDarkPrimary'}>
                                {cornerHeader}
                            </Typography>
                        </DynamicGridHeaderCell>

                        {headerColumns.map((column) => {
                            const colSpan = column.subColumns.length;

                            return (
                                <DynamicGridHeaderCell
                                    key={`col-header-${column.columnLabel}`}
                                    className={`${classes.cell} ${classes.borderLeft}`}
                                    contentClassName={classes.headerContent}
                                    colSpan={colSpan}
                                >
                                    <Typography
                                        style={{ fontWeight: 500 }}
                                        color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                    >
                                        {column.columnLabel}
                                    </Typography>
                                </DynamicGridHeaderCell>
                            );
                        })}
                    </DynamicGridRow>

                    <DynamicGridRow className={classes.subRowHeader}>
                        <DynamicGridHeaderCell contentClassName={classes.headerContent}>
                            <Typography style={{ fontWeight: 500 }} color={readOnly ? 'grayFive' : 'textDarkPrimary'}>
                                Value
                            </Typography>
                        </DynamicGridHeaderCell>

                        {headerColumns.flatMap((column) => {
                            return column.subColumns.map((subColumn) => (
                                <DynamicGridHeaderCell
                                    key={`subcol-header-${column.columnLabel}-${subColumn.columnOrder}-${subColumn.subColumnLabel}`}
                                    className={classes.subColumnCell}
                                    contentClassName={classes.headerContent}
                                >
                                    <Typography
                                        numberOfLines={1}
                                        style={{ fontWeight: 500 }}
                                        color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                    >
                                        {subColumn.subColumnLabel}
                                    </Typography>
                                </DynamicGridHeaderCell>
                            ));
                        })}
                    </DynamicGridRow>
                </thead>

                <tbody>
                    {rows.map((row) => (
                        <DynamicGridRow key={`row-${row.rowOrder}`} className={classes.bodyRow}>
                            <DynamicGridHeaderCell contentClassName={classes.headerContent}>
                                <Typography
                                    style={{ fontWeight: 500 }}
                                    color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                >
                                    {row.rowLabel}
                                    {row.columns.some((column) =>
                                        column.subColumns.some((subColumn) => subColumn.isRequired)
                                    ) && <Typography color="redOne"> *</Typography>}
                                </Typography>
                            </DynamicGridHeaderCell>

                            {row.columns.flatMap((column) => {
                                return column.subColumns.map((subColumn) => {
                                    const cellKey = `row-${row.rowOrder}-col-${column.columnLabel}-sub-${subColumn.columnOrder}-${subColumn.subColumnLabel}`;
                                    const isInvalid = isCellInvalid?.(subColumn.worksheetElementGridId);
                                    const value =
                                        answers.find(
                                            (cellAnswer) =>
                                                cellAnswer.worksheetElementGridId === subColumn.worksheetElementGridId
                                        )?.value ?? '';

                                    return (
                                        <DynamicGridCell
                                            key={`cell-${cellKey}`}
                                            onClick={focusTextarea}
                                            className={classes.borderLeft}
                                            invalid={isInvalid}
                                            readOnly={readOnly}
                                        >
                                            <AutoSizeTextarea
                                                id={`input-${subColumn.worksheetElementGridId}`}
                                                value={value}
                                                disabled={readOnly}
                                                required={subColumn.isRequired}
                                                onChange={(value) => {
                                                    onChange?.(subColumn.worksheetElementGridId, value);
                                                }}
                                                onBlur={() => {
                                                    onBlur?.(subColumn.worksheetElementGridId);
                                                }}
                                                placeholder={isInvalid ? 'Required' : ''}
                                            />
                                        </DynamicGridCell>
                                    );
                                });
                            })}
                        </DynamicGridRow>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
